<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>

    <ModalPopUp :modalActive="generateState.generating" :allowClose="false" :backgroundCloseModal="false">
      <template v-slot:modalContent>
        <ApiLoading :apiError="false" :loadingMessage="referenceData.generatingMessageText" isMemberPortal />
      </template>
    </ModalPopUp>

    <RecordsView :apiStatus="apiStatus" :recordsData="recordsData" :referenceData="referenceData" :isExternalLink="true" :recordLink="recordLink" :includeRecordHeader="false">
      <template v-if="referenceData.showGenerateButton" v-slot:paragraphData>
        <div class="generate-tax-statement">
          <div v-if="referenceData.atoDisclaimerText" class="ato-disclaimer fr-view" v-html="referenceData.atoDisclaimerText"></div>

          <Alert v-if="generateState.success" alertType="success" class="inline-message" :message="referenceData.successMessageText" :isPlainText="false" />
          <Alert v-if="generateState.timeoutError" alertType="error" class="inline-message" :message="referenceData.timeoutErrorMessageText" :isPlainText="false" />
          <Alert v-if="generateState.noDataError" alertType="error" class="inline-message" :message="referenceData.noDataMessageText" :isPlainText="false" />
          <Alert v-if="generateState.error" alertType="error" class="inline-message" :message="referenceData.errorMessageText" :isPlainText="false" />
          <Alert v-if="isRateLimited" alertType="info" class="inline-message" :message="referenceData.rateLimitMessageText" :isPlainText="false" />

          <div class="generate-controls">
            <FormField labelText="Financial year" inputId="financial-year-select">
              <DropdownSelect
                id="financial-year-select"
                :items="financialYearOptions"
                :initialSelect="selectedFinancialYearLabel"
                v-model="selectedFinancialYear"
              />
            </FormField>

            <button
              class="btn btn-primary"
              :disabled="isGenerateDisabled"
              @click="handleGenerate"
            >
              {{ referenceData.generateButtonText }}
            </button>
          </div>
        </div>
      </template>

      <template v-slot:pagination>
        <Pagination isSPA @toPageNumber="paginationNavigation" :endPage="paginationParams.lastPageNum" :currentPageFromParent="paginationParams.pageNum" />
      </template>
    </RecordsView>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { format } from 'date-fns';
import orderBy from 'lodash.orderby';
import { VUEX_TAX_STATEMENTS, VUEX_GENERAL } from '@/constants/VuexConstants';

// EAI messageCode returned when the policy/financial year has no data to generate a tax statement for.
const NO_DATA_MESSAGE_CODE = 'GEN-OD-TAX-STMT-008';
import { MY_COVER_TAX_STATEMENTS, CONTACT_US, ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';
import { getAccessLevel, getCoveredAs, initiatePdfFileDownload } from '@/utils';
import { AccessLevel, NonDependantFlexCodes } from '@/enums';
import RecordsView from '@/components/RecordsView.vue';
import Pagination from '@general/js/components/Pagination.vue';
import TaxStatementsService from '@/services/TaxStatements';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import ModalPopUp from '@/components/ModalPopUp.vue';
import ApiLoading from '@/components/ApiLoading.vue';
import FormField from '@/components/FormField.vue';
import Alert from '@/components/Alert.vue';

export default {
  name: 'TaxStatements',

  components: {
    Pagination,
    RecordsView,
    DropdownSelect,
    ModalPopUp,
    ApiLoading,
    FormField,
    Alert,
  },

  data() {
    return {
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        recordData: {
          fail: false,
          loading: false,
        },
        recordDocument: {
          fail: false,
          loading: false,
        },
      },
      MY_COVER_TAX_STATEMENTS,
      CONTACT_US,
      recordsData: {
        headings: ['Financial year', 'Member', 'Statement date'],
        data: [],
        noRecordsText: 'You currently do not have any tax statements available',
      },
      taxStatementData: [],
      numOfYears: 5, // Up to 5 years worth of tax statements will be displayed.
      paginationParams: {
        pageNum: 1,
        pageSize: 10,
        totalNum: 0,
        lastPageNum: 1,
      },
      referenceData: {
        informationText: undefined,
        showGenerateButton: false,
        financialYearFrom: 0,
        financialYearTo: 0,
        generateButtonText: undefined,
        generatingMessageText: undefined,
        errorMessageText: undefined,
        successMessageText: undefined,
        timeoutErrorMessageText: undefined,
        rateLimitMessageText: undefined,
        noDataMessageText: undefined,
        atoDisclaimerText: undefined,
        lastTaxStatementGenerations: {},
      },
      generateState: {
        generating: false,
        error: false,
        timeoutError: false,
        noDataError: false,
        success: false,
      },
      selectedFinancialYear: null,
      accessLevel: '',
      coveredAs: '',
      nonDependantList: Object.values(NonDependantFlexCodes),
      recordLink: {
        recordLinkFunction: this.fetchTaxStatementDocument,
        recordLinkParams: 'documentId',
      },
      urlDataToCleanup: [],
    };
  },

  beforeMount() {
    this.getMemberDetails();

    if (!this.hasAccessToTaxPage) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }
  },

  mounted() {
    this.getReferenceData();
    this.fetchTaxStatementsData();
  },

  beforeUnmount() {
    // Explicitly unload URL for optimal performance and memory usage
    this.urlDataToCleanup.forEach((url) => {
      URL.revokeObjectURL(url);
    });
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      memberships: VUEX_GENERAL.GETTERS.memberships,
    }),

    hasAccessToTaxPage() {
      // Covered as Policy Holder or Spouse (and any of the partner type)
      if (this.nonDependantList.includes(this.coveredAs)) {
        return true;
      }
      // Covered as Dependants (and any of the dependant types) and has Full Access
      if (!this.nonDependantList.includes(this.coveredAs) && this.accessLevel === AccessLevel.FULL_ACCESS) {
        return true;
      }
      return false;
    },

    errorPageRouter() {
      return this.$router.resolve({ name: ERROR_404_PAGE_NOT_FOUND });
    },

    /**
     * Derives the current Australian financial year ending year.
     * Australian FY: 1 July – 30 June.
     * If month >= July (month index 6), ending year = currentYear + 1, else = currentYear.
     */
    currentFinancialYearEndingYear() {
      const now = new Date();
      return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
    },

    /**
     * Builds the financial year dropdown options from FinancialYearFrom through to FinancialYearTo.
     * FinancialYearFrom is the ending year of the earliest FY (e.g. 2025 → "2024/25").
     * FinancialYearTo is the ending year of the latest FY (e.g. 2026 → "2025/26").
     * Options are ordered newest-first.
     */
    financialYearOptions() {
      const from = this.referenceData.financialYearFrom;
      const to = this.referenceData.financialYearTo || this.currentFinancialYearEndingYear;
      if (!from || from > to) return [];

      const options = [];
      for (let endYear = to; endYear >= from; endYear--) {
        const startYear = endYear - 1;
        const name = `${startYear}/${String(endYear).slice(-2)}`;
        options.push({ value: String(endYear), name });
      }
      return options;
    },

    /**
     * True if the member has already generated a tax statement for the selected FY today.
     * Derived purely from lastTaxStatementGenerations — no manual state needed.
     */
    isRateLimited() {
      if (!this.selectedFinancialYear) return false;
      return this.isTodayRateLimited(this.selectedFinancialYear);
    },

    /**
     * Whether the generate button should be disabled.
     * Disabled while a generation is in progress, or if the member has already generated
     * for the selected financial year today (server-side rate limit).
     */
    isGenerateDisabled() {
      return this.generateState.generating || this.isRateLimited || this.generateState.error || this.generateState.timeoutError || this.generateState.noDataError;
    },

    selectedFinancialYearLabel() {
      const match = this.financialYearOptions.find((o) => o.value === this.selectedFinancialYear);
      return match ? match.name : 'Select a financial year';
    },
  },

  watch: {
    selectedFinancialYear() {
      // Clear the error message when the user changes FY.
      // Rate-limit state (isRateLimited) is a computed and updates automatically.
      this.generateState.error = false;
      this.generateState.noDataError = false;
      this.generateState.success = false;
    },
  },

  methods: {
    ...mapMutations('RebateNomination', {
      resetState: VUEX_TAX_STATEMENTS.MUTATIONS.resetState,
    }),

    /**
     * Converts a list of members information (containing tax) to a list of tax statements for all members
     */
    formatTaxStatementsList(membersList) {
      let taxStatements = [];
      let filteredTaxStatements;
      const currentPersonCode = this.personCode;

      membersList.forEach((memberObj) => {
        taxStatements = taxStatements.concat(
          // maps each tax statement from a member
          memberObj.taxStatements.map((taxObj) => {
            return {
              'Financial year': this.formatFinancialYear(taxObj.financialYear),
              Member: `${memberObj.firstName} ${memberObj.lastName}`,
              'Statement date': format(new Date(taxObj.dateGenerated), 'dd/MM/yyyy'), // Format date from '2019-05-22T13:05:05' to 'DD/MM/YYYY'
              documentId: taxObj.documentId,
              personCode: memberObj.personCode,
            };
          }),
        );
      });

      // For spouse with self access, only display him/herself tax statement
      if (this.nonDependantList.includes(this.coveredAs) && this.coveredAs !== NonDependantFlexCodes.POLICY_HOLDER && this.accessLevel === AccessLevel.SELF_ACCESS) {
        filteredTaxStatements = taxStatements.filter((taxObj) => {
          return taxObj.personCode === currentPersonCode;
        });
      } else {
        filteredTaxStatements = [...taxStatements];
      }

      // As a dependant with full access, the order in each year will be alphabetically ordered according to the Policy Holder / Partner name.
      if (!this.nonDependantList.includes(this.coveredAs) && this.accessLevel === AccessLevel.FULL_ACCESS) {
        return orderBy(filteredTaxStatements, ['Financial year', 'Member'], ['desc', 'asc']);
      }

      // If two tax statements have the same year, statements will be ordered with logged in user as priority.
      return orderBy(
        filteredTaxStatements,
        [
          'Financial year',
          function (record) {
            return record.personCode === currentPersonCode;
          },
        ],
        ['desc', 'desc'],
      );
    },

    // Format financial year form 'YYYY/YYYY' to "YYYY/YY", e.g. "2019/2020" to "2019/20"
    formatFinancialYear(yearString) {
      return `${yearString.slice(0, 5)}${yearString.slice(-2)}`;
    },

    getMemberDetails() {
      this.accessLevel = getAccessLevel(this.memberNumber, this.memberships)?.toLowerCase();
      this.coveredAs = getCoveredAs(this.memberNumber, this.memberships)?.toUpperCase();
    },

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const refData = await TaxStatementsService.getReferenceData();
        if (refData?.taxStatementsInformationText?.informationText) {
          // return member number string token swapped data
          this.referenceData.informationText = refData.taxStatementsInformationText.informationText.tokenSwap();
        }
        if (refData?.showGenerateButton !== undefined) {
          this.referenceData.showGenerateButton = refData.showGenerateButton;
        }
        if (refData?.financialYearFrom) {
          this.referenceData.financialYearFrom = refData.financialYearFrom;
        }
        if (refData?.financialYearTo) {
          this.referenceData.financialYearTo = refData.financialYearTo;
        }
        if (refData?.generateButtonText?.informationText) {
          this.referenceData.generateButtonText = refData.generateButtonText.informationText;
        }
        if (refData?.generatingMessageText?.informationText) {
          this.referenceData.generatingMessageText = refData.generatingMessageText.informationText;
        }
        if (refData?.errorMessageText?.informationText) {
          this.referenceData.errorMessageText = refData.errorMessageText.informationText;
        }
        if (refData?.successMessageText?.informationText) {
          this.referenceData.successMessageText = refData.successMessageText.informationText;
        }
        if (refData?.timeoutErrorMessageText?.informationText) {
          this.referenceData.timeoutErrorMessageText = refData.timeoutErrorMessageText.informationText;
        }
        if (refData?.rateLimitMessageText?.informationText) {
          this.referenceData.rateLimitMessageText = refData.rateLimitMessageText.informationText;
        }
        if (refData?.noDataMessageText?.informationText) {
          this.referenceData.noDataMessageText = refData.noDataMessageText.informationText;
        }
        if (refData?.atoDisclaimerText?.informationText) {
          this.referenceData.atoDisclaimerText = refData.atoDisclaimerText.informationText;
        }
        if (refData?.lastTaxStatementGenerations) {
          this.referenceData.lastTaxStatementGenerations = refData.lastTaxStatementGenerations;
        }

        // Pre-select the current financial year in the dropdown.
        // isRateLimited computed will automatically reflect the correct state for this FY.
        if (this.financialYearOptions.length > 0) {
          this.selectedFinancialYear = this.financialYearOptions[0].value;
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async fetchTaxStatementsData() {
      this.apiStatus.recordData.loading = true;
      this.apiStatus.recordData.fail = false;

      // Fetch reference data and tax statement summary data
      try {
        const taxData = await TaxStatementsService.getTaxStatementSummary(this.memberNumber, this.numOfYears);
        this.taxStatementData = this.formatTaxStatementsList(taxData);
        this.recordsData.data = this.taxStatementData.slice(0, this.paginationParams.pageSize);
        this.paginationParams.totalNum = this.taxStatementData.length;
        this.paginationParams.lastPageNum = Math.ceil(this.paginationParams.totalNum / this.paginationParams.pageSize);
      } catch (error) {
        this.apiStatus.recordData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.recordData.loading = false;
      }
    },

    async fetchTaxStatementDocument(documentId) {
      this.apiStatus.recordDocument.fail = false;
      this.apiStatus.recordDocument.loading = true;

      const documentBeingAccessed = this.recordsData?.data.find((record) => {
        return record.documentId === documentId;
      });

      const accessingPersonCode = documentBeingAccessed.personCode;
      const docName = documentBeingAccessed.Document;

      try {
        // personCode means accessingPersonCode
        const fileData = await TaxStatementsService.getTaxStatementDocument(this.memberNumber, accessingPersonCode, documentId);
        initiatePdfFileDownload(fileData, docName, this.urlDataToCleanup);
      } catch (error) {
        this.apiStatus.recordDocument.fail = true;

        window.open(this.errorPageRouter.href, '_blank'); // Open error page in a new tab
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.recordDocument.loading = false;
      }
    },

    paginationNavigation(pageNum) {
      this.paginationParams.pageNum = Number(pageNum);

      this.recordsData.data = this.taxStatementData.slice(
        (this.paginationParams.pageNum - 1) * this.paginationParams.pageSize,
        this.paginationParams.pageNum * this.paginationParams.pageSize,
      );
    },

    /**
     * Returns true if the member has already generated a tax statement for the given financial year today.
     * @param {string} financialYear - Financial year ending year as a string, e.g. "2025".
     */
    isTodayRateLimited(financialYear) {
      const lastGeneration = this.referenceData.lastTaxStatementGenerations?.[financialYear];
      if (!lastGeneration) return false;

      const lastDate = new Date(lastGeneration);
      const today = new Date();
      return (
        lastDate.getFullYear() === today.getFullYear() &&
        lastDate.getMonth() === today.getMonth() &&
        lastDate.getDate() === today.getDate()
      );
    },

    /**
     * Handles the Generate Tax Statement button click.
     * 1. Records a generatedSinceTimestamp and disables the button.
     * 2. POSTs to generate the tax statement.
     * 3. On success, fetches the updated summary with the timestamp.
     * 4. On any error, shows the error message; button stays disabled.
     */
    async handleGenerate() {
      if (this.isGenerateDisabled) return;

      // Client-side timestamp captured before the call — used for optimistic UI rate-limiting only.
      const clientTimestamp = new Date().toISOString();

      this.generateState.generating = true;
      this.generateState.error = false;
      this.generateState.timeoutError = false;
      this.generateState.noDataError = false;
      this.generateState.success = false;

      // Record this generation locally BEFORE the API call — mirrors the server, which now saves
      // the generation time before the EAI call. This ensures isRateLimited stays true in the
      // current session even if the EAI call fails.
      this.referenceData.lastTaxStatementGenerations[this.selectedFinancialYear] = clientTimestamp;

      try {
        const timeoutMs = 30000;
        const withTimeout = (promise) => Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs)),
        ]);

        // Server-side timestamp returned by the generate API — used for EAI blob polling.
        const generatedSinceTimestamp = await withTimeout(
          TaxStatementsService.generateTaxStatement(this.memberNumber, this.selectedFinancialYear)
        );

        // Pass the server-side timestamp so EAI polls blob for documents newer than
        // the exact moment the generate request was processed on the server.
        const taxData = await withTimeout(
          TaxStatementsService.getTaxStatementSummary(this.memberNumber, this.numOfYears, undefined, { generatedSinceTimestamp })
        );
        this.taxStatementData = this.formatTaxStatementsList(taxData);
        this.recordsData.data = this.taxStatementData.slice(0, this.paginationParams.pageSize);
        this.paginationParams.totalNum = this.taxStatementData.length;
        this.paginationParams.lastPageNum = Math.ceil(this.paginationParams.totalNum / this.paginationParams.pageSize);
        this.paginationParams.pageNum = 1;
        this.generateState.success = true;
      } catch (error) {
        if (error.message === 'TIMEOUT') {
          this.generateState.timeoutError = true;
        } else if (error.response?.data?.messageCode === NO_DATA_MESSAGE_CODE) {
          this.generateState.noDataError = true;
        } else {
          this.generateState.error = true;
        }
        logger.log(error.message ?? error);
      } finally {
        this.generateState.generating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_responsive_mixins.scss';

:deep(.top-content) {
  #main-content {
    p:last-child {
      margin-bottom: 0;
    }
  }
}

.generate-tax-statement {
  margin-bottom: 2rem;

  .ato-disclaimer {
    margin-bottom: 1.5rem;
  }

  .generate-controls {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-bottom: 1rem;

    @include tablet-and-above {
      flex-direction: row;
      align-items: flex-end;
      gap: 1rem;
    }

    // FormField root element inherits the parent scoped attribute in Vue 2,
    // so it can be targeted without ::v-deep.
    .form-field {
      @include tablet-and-above {
        width: 250px;
        flex-shrink: 0;
        margin-bottom: 0;
      }
    }

    .btn {
      @include tablet-and-above {
        white-space: nowrap;
        flex-shrink: 0;
      }
    }
  }
}
</style>
